import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Address } from 'viem'
import { useAccount } from 'wagmi'
import { useAvailableSubAccount } from '@/domain/euler/positions/useAvailableSubAccount'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { BORROW_LTV_SAFETY_FACTOR, MAX_LTV_EPSILON } from './borrowMoreConstants'

const MAX_DISPLAY_DECIMALS = 6

function floorToDecimals(value: number, decimals: number): string {
  const precision = Math.min(decimals, MAX_DISPLAY_DECIMALS)
  const factor = 10 ** precision
  return (Math.floor(value * factor) / factor).toFixed(precision)
}

interface UseEBorrowMoreDialogParams {
  liabilityVault: Address
  liabilitySymbol: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  currentDebtFormatted: number
  currentLTV: number
  maxLTV: number
  collateralValueUSD: number
  liabilityPriceUSD: number
  subAccountId?: number
}

export interface UseEBorrowMoreDialogResult {
  liabilityToken: Token
  additionalBorrowAmount: string
  setAdditionalBorrowAmount: (amount: string) => void
  ltv: number
  setLtv: (ltv: number) => void
  canBorrow: boolean
  impliedLtv: number
  safeMaxLTV: number
  subAccountAddress: Address | undefined
}

export function useEBorrowMoreDialog({
  liabilityVault,
  liabilitySymbol,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  currentDebtFormatted,
  currentLTV,
  maxLTV,
  collateralValueUSD,
  liabilityPriceUSD,
  subAccountId: propSubAccountId,
}: UseEBorrowMoreDialogParams): UseEBorrowMoreDialogResult {
  const { address: userAddress } = useAccount()

  const { hasExistingPosition, existingSubAccountId } = useAvailableSubAccount({
    liabilityVault,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!userAddress && propSubAccountId === undefined,
  })

  const effectiveSubAccountId = propSubAccountId ?? existingSubAccountId
  const effectiveHasPosition = propSubAccountId !== undefined ? true : hasExistingPosition

  const subAccountAddress =
    effectiveHasPosition && effectiveSubAccountId !== null && userAddress
      ? getSubAccountAddress(userAddress, effectiveSubAccountId)
      : undefined

  const safeMaxLTV = maxLTV * BORROW_LTV_SAFETY_FACTOR

  const initialLTV = useMemo(() => {
    const targetLTV = currentLTV + 1
    return Math.min(targetLTV, safeMaxLTV)
  }, [currentLTV, safeMaxLTV])

  const initialAdditionalBorrow = useMemo(() => {
    if (collateralValueUSD <= 0 || liabilityPriceUSD <= 0) {
      return '0'
    }
    const targetDebtUSD = (collateralValueUSD * initialLTV) / 100
    const targetDebtAmount = targetDebtUSD / liabilityPriceUSD
    const additionalBorrow = Math.max(0, targetDebtAmount - currentDebtFormatted)
    return floorToDecimals(additionalBorrow, liabilityDecimals)
  }, [collateralValueUSD, liabilityPriceUSD, initialLTV, currentDebtFormatted, liabilityDecimals])

  const [additionalBorrowAmount, setAdditionalBorrowAmount] = useState(initialAdditionalBorrow)
  const [ltv, setLtv] = useState(initialLTV)
  const isLtvDrivenUpdateRef = useRef(false)

  const liabilityToken = useMemo(
    () =>
      new Token({
        symbol: TokenSymbol(liabilitySymbol || 'UNKNOWN'),
        name: liabilitySymbol || 'Unknown',
        decimals: liabilityDecimals,
        address: CheckedAddress(liabilityAsset),
        unitPriceUsd: String(liabilityPriceUSD),
        logoURI: liabilityLogoURI,
      }),
    [liabilitySymbol, liabilityDecimals, liabilityAsset, liabilityPriceUSD, liabilityLogoURI],
  )

  const totalDebt = currentDebtFormatted + Number(additionalBorrowAmount)
  const impliedLtv =
    collateralValueUSD > 0 && liabilityPriceUSD > 0 ? (totalDebt * liabilityPriceUSD * 100) / collateralValueUSD : 0
  const canBorrow =
    Number(additionalBorrowAmount) > 0 && impliedLtv >= currentLTV && impliedLtv <= safeMaxLTV + MAX_LTV_EPSILON

  useEffect(() => {
    if (isLtvDrivenUpdateRef.current) {
      isLtvDrivenUpdateRef.current = false
      return
    }
    if (collateralValueUSD > 0 && liabilityPriceUSD > 0) {
      const newDebtUSD = totalDebt * liabilityPriceUSD
      const newLTV = (newDebtUSD / collateralValueUSD) * 100
      const nextLtv = Math.min(newLTV, safeMaxLTV)
      if (Math.abs(nextLtv - ltv) < 0.0001) return
      setLtv(nextLtv)
    }
  }, [totalDebt, collateralValueUSD, liabilityPriceUSD, safeMaxLTV, ltv])

  const handleLtvChange = (newLtv: number) => {
    setLtv(newLtv)
    if (collateralValueUSD > 0 && liabilityPriceUSD > 0) {
      const newDebtUSD = (collateralValueUSD * newLtv) / 100
      const newTotalDebt = newDebtUSD / liabilityPriceUSD
      const newAdditionalBorrow = Math.max(0, newTotalDebt - currentDebtFormatted)
      isLtvDrivenUpdateRef.current = true
      setAdditionalBorrowAmount(floorToDecimals(newAdditionalBorrow, liabilityDecimals))
    }
  }

  return {
    liabilityToken,
    additionalBorrowAmount,
    setAdditionalBorrowAmount,
    ltv,
    setLtv: handleLtvChange,
    canBorrow,
    impliedLtv,
    safeMaxLTV,
    subAccountAddress,
  }
}
